import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormControl } from '@mui/material';
import CustomFieldWrapper from './CustomFieldWrapper';
import debounce from 'lodash/debounce';

const MultiLineTextFieldComponent = ({
  fieldId, // Treated as UFI
  fieldMetadata = {}, // Default to an empty object if not provided
  value: propValue,
  onChange,
  onBlur,            // 🔹 Forwarded from FieldRenderer
  onFocus,           // 🔹 Forwarded from FieldRenderer
  disabled = false,
  isSubmitting = false,
  isBuilderMode = false, // new prop to determine the context
  setShowDropZones, // function to control the visibility of drop zones
  variant,
  error, // Pass error prop from FieldRenderer
  helperText, // Pass helperText from FieldRenderer
  externalHelperText, // Pass externalHelperText from FieldRenderer
}) => {
  const [internalValue, setInternalValue] = useState(propValue);

  useEffect(() => {
    setInternalValue(propValue);
  }, [propValue]);

  // Memoize transformations to avoid unnecessary re-renders
  const transformations = useMemo(() => fieldMetadata.transformations || {}, [fieldMetadata.transformations]);

  const applyTransformations = useCallback((inputValue) => {
    const { capitalize, spacing, allowSymbols } = transformations;

    let transformedValue = inputValue;

    // Apply transformations based on provided configurations
    switch (capitalize) {
      case 'first-letter':
        transformedValue = transformedValue.charAt(0).toUpperCase() + transformedValue.slice(1).toLowerCase();
        break;
      case 'all-caps':
        transformedValue = transformedValue.toUpperCase();
        break;
      case 'no-caps':
        transformedValue = transformedValue.toLowerCase();
        break;
      default:
        break;
    }

    switch (spacing) {
      case 'underscore':
        transformedValue = transformedValue.replace(/\s+/g, '_');
        break;
      case 'hyphen':
        transformedValue = transformedValue.replace(/\s+/g, '-');
        break;
      case 'no-spacing':
        transformedValue = transformedValue.replace(/\s+/g, '');
        break;
      default:
        break;
    }

    if (!allowSymbols) {
      transformedValue = transformedValue.replace(/[^\w\s-]/gi, '');
    }

    return transformedValue;
  }, [transformations]);

  // Debounced notify → keeps FieldRenderer localValue in sync while typing (without spamming)
  const debouncedOnChange = useCallback(
    debounce((event) => {
      onChange({
        ...event,
        target: {
          ...event.target,
          value: event.target.value,
        },
      });
    }, 300),
    [onChange]
  );

  const handleChange = useCallback(
    (event) => {
      let inputValue = event.target.value;
      inputValue = applyTransformations(inputValue);

      if (fieldMetadata.maxLength && inputValue.length > fieldMetadata.maxLength) {
        inputValue = inputValue.substring(0, fieldMetadata.maxLength);
      }

      setInternalValue(inputValue);
      debouncedOnChange({ ...event, target: { ...event.target, value: inputValue } });

      if (isBuilderMode) {
        setShowDropZones(false);
      }
    },
    [applyTransformations, debouncedOnChange, fieldMetadata.maxLength, isBuilderMode, setShowDropZones]
  );

  const handleBlur = useCallback(() => {
    if (isBuilderMode) {
      setShowDropZones(true);
    }

    // 1) Ensure latest transformed value is sent up (so FieldRenderer.localValue stays current)
    onChange({ target: { value: internalValue } });

    // 2) VERY IMPORTANT: bubble the blur so FieldRenderer.handleBlur commits setAnswer(ufi, localValue)
    if (typeof onBlur === 'function') {
      try { onBlur(); } catch (_) {}
    }
  }, [isBuilderMode, setShowDropZones, internalValue, onChange, onBlur]);

  // Handle submission logic with optional encoding
  useEffect(() => {
    if (isSubmitting) {
      const finalValue = transformations.encode ? encodeURIComponent(internalValue) : internalValue;
      // Keep shape consistent with other paths (FieldRenderer expects event.target.value or raw)
      onChange({ target: { value: finalValue } });
    }
  }, [isSubmitting, internalValue, onChange, fieldId, transformations.encode]);

  // Set multi-line related props
  const multiline = true; // Always true for multi-line component
  const lineCount = fieldMetadata.lineCount || 3; // Default lineCount to 3 if not specified

  // Log the values being passed to CustomFieldWrapper
//  console.log("[MultiLineTextFieldComponent] Passing props to CustomFieldWrapper:", {
//    multiline,
//    lineCount,
//    variant,
//    internalValue,
//  });

  return (
    <FormControl fullWidth disabled={disabled || isSubmitting}>
      <CustomFieldWrapper
        fieldId={fieldId} // 🔹 Keep for better logs (and wrapper behaviors)
        label={fieldMetadata.label || 'Multi-line Field'}
        variant={variant}
        value={internalValue}
        onChange={handleChange}
        onFocus={onFocus}   // 🔹 Forward parent onFocus so FieldRenderer sets isEditing=true
        onBlur={handleBlur} // 🔹 Local blur (calls parent onBlur internally)
        required={fieldMetadata.required}
        error={error}
        helperText={helperText}
        externalHelperText={externalHelperText}
        placeholder={fieldMetadata.hint || 'Enter text'}
        fullWidth
        multiline={multiline} // Enable multi-line input
        rows={lineCount}      // Set the number of lines for multi-line input
        InputProps={{
          inputProps: {
            maxLength: fieldMetadata.maxLength,
          },
        }}
        disabled={disabled || isSubmitting}
      />
    </FormControl>
  );
};

MultiLineTextFieldComponent.propTypes = {
  fieldId: PropTypes.string.isRequired,
  fieldMetadata: PropTypes.shape({
    label: PropTypes.string,
    required: PropTypes.bool,
    maxLength: PropTypes.number,
    externalError: PropTypes.bool,
    externalHelperText: PropTypes.string,
    helperText: PropTypes.string,
    hint: PropTypes.string,
    transformations: PropTypes.object,
    lineCount: PropTypes.number, // Define line count for multi-line input
  }),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,    // 🔹 Added
  onFocus: PropTypes.func,   // 🔹 Added
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isBuilderMode: PropTypes.bool,
  setShowDropZones: PropTypes.func, // Ensure this prop type is declared if used
  variant: PropTypes.string, // Add variant to PropTypes
  error: PropTypes.bool, // Ensure error prop is passed
  helperText: PropTypes.string, // Ensure helperText prop is passed
  externalHelperText: PropTypes.string, // External helper text for validation errors
};

export default MultiLineTextFieldComponent;
